import {
  Component,
  inject,
  input,
  InputSignal,
} from '@angular/core';
import {MatTooltipModule} from '@angular/material/tooltip';
import {
  MatBottomSheet,
  MatBottomSheetModule,
} from '@angular/material/bottom-sheet';
import {TranslateService} from '@ngx-translate/core';
import {
  AnalyticDataInfo,
  NumericComparisonMetrics,
  StringComparisonMetrics,
} from '../../store/portal/portal.model';
import {RemarkBottomSheetComponent} from '../remark-bottom-sheet/remark-bottom-sheet.component';
import {Constants} from '../../constants/constants';
import {PasteApostropheAfterEachThreeNumsPipe} from '../../pipes/paste-apostrophe-after-each-three-nums.pipe';
import {PasteApostropheWithFixedDecimalsPipe} from '../../pipes/paste-apostrophe-with-fixed-decimals.pipe';
import {RoundPercentagePipe} from '../../pipes/round-percentage.pipe';

enum PensionType {
  FULL_CONTRACT = 'FULL_CONTRACT',
  AUTONOMOUS = 'AUTONOMOUS',
  SAVINGS_RISKS = 'SAVINGS_RISKS',
  INDIVIDUAL = 'INDIVIDUAL',
}

@Component({
  selector: 'app-analytics-table',
  imports: [
    MatTooltipModule,
    MatBottomSheetModule,
    PasteApostropheAfterEachThreeNumsPipe,
    PasteApostropheWithFixedDecimalsPipe,
    RoundPercentagePipe,
  ],
  templateUrl: './analytics-table.component.html',
  styleUrl: './analytics-table.component.scss',
})
export class AnalyticsTableComponent {

  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);

  private readonly translateService: TranslateService = inject(TranslateService);

  public readonly pdfMode: InputSignal<boolean> = input<boolean>(false);

  public readonly iOsMode: InputSignal<boolean> = input<boolean>(false);

  public readonly rootTranslationKey: InputSignal<string> = input<string>('');

  public readonly tableData: InputSignal<AnalyticDataInfo | undefined> = input<AnalyticDataInfo>();

  public readonly labels: InputSignal<Record<string, string> | undefined> = input<Record<string, string>>();

  protected readonly Constants = Constants;

  getDataKeys(): string[] {
    return Object.keys(this.tableData() ?? {});
  }

  isPensionInfoData(): boolean {
    const tableData = this.tableData();
    return this.getDataKeys().some((key: string) => !!tableData?.[key]
      && !Object.hasOwn(tableData[key], 'percent'));
  }

  isEmployeeContributionsPctData(key: string): boolean {
    return key === 'employeeContributionsPct';
  }

  isCostsAndFinancingData(): boolean {
    return this.rootTranslationKey() === Constants.PREMIUM_ROOT_TRANSLATION_KEY;
  }

  getStringValues(key: string): StringComparisonMetrics {
    return this.tableData()?.[key];
  }

  getNumericValue(key: string): NumericComparisonMetrics {
    return this.tableData()?.[key];
  }

  isMobileView(): boolean {
    const mobileViewWidth = 480; // Adjust this value based on your mobile breakpoint
    return window.innerWidth < mobileViewWidth;
  }

  openBottomSheet(title: string, contentText: string): void {
    this.bottomSheet.open(RemarkBottomSheetComponent, {
      data: {
        title,
        contentText,
        closeLabel: this.translateService.instant('title.close'),
      },
    });
  }

  getDifferenceLabel(metrics: StringComparisonMetrics): string {
    if (!metrics?.current) {
      return Constants.DASH;
    }
    return metrics.difference
      ? this.getTranslationKeyByKey('diff.changed')
      : this.getTranslationKeyByKey('diff.unchanged');
  }

  getTranslationValue(key: string | null): string {
    if (!key) {
      return Constants.DASH;
    }
    if (!Object.keys(PensionType).includes(key)) {
      return key;
    }
    const label = this.labels()?.[`${this.rootTranslationKey()}.contractType.${key.toLowerCase()}`];
    return label ? this.translateService.instant(label) : '';
  }

  getTranslationKeyByKey(key: string): string {
    const labels = this.labels();
    return labels?.[key] ? this.translateService.instant(labels?.[key]) : '';
  }

  getTranslationTitle(key: string): string {
    const rootTranslationKey = this.rootTranslationKey();
    const labels = this.labels();
    return labels?.[`${rootTranslationKey}${Constants.DOT}${key}`]
      ? this.translateService.instant(labels?.[`${rootTranslationKey}${Constants.DOT}${key}`]) : '';
  }

  getTranslationForRemark(key: string): string {
    const translationKey = `analytics.${this.rootTranslationKey()}${Constants.DOT}${key}.remark`;
    const translatedLabel = this.translateService.instant(translationKey);
    return translatedLabel !== translationKey ? translatedLabel : '';
  }

}
